import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { submitOfferRevisionForReviewAsync } from "../../../slices/offer";
import {
  isOfferFeatureEnabled,
  resolveOfferActionErrorMessage,
} from "./utils";

/**
 * Resolve the revision to submit for internal review:
 * current draft id, else latest draft / pending_review.
 */
function resolveSubmittableRevisionId(offer, revisions = []) {
  const draftId = offer?.current_draft_revision_id;
  if (draftId) return draftId;

  const sorted = [...revisions].sort(
    (a, b) => (b.revision_number || 0) - (a.revision_number || 0)
  );
  const candidate = sorted.find((r) =>
    ["draft", "pending_review"].includes(r?.status)
  );
  return candidate?.id || null;
}

/**
 * Internal «Send til kontroll» — submit_for_review (pending_review).
 * Distinct from OfferSendDialog (customer send).
 */
export default function OfferSubmitForReviewDialog({
  open,
  onClose,
  offer,
  featureFlags,
  onRefresh,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const storeRevisions = useSelector((state) => state.offers?.revisions || []);
  const revisionsOfferId = useSelector(
    (state) => state.offers?.revisionsOfferId
  );
  const flags = featureFlags || offer?.offer_feature_flags || {};
  const revisionsEnabled = isOfferFeatureEnabled(flags, "offer_revisions");

  const revisions =
    revisionsOfferId === offer?.id
      ? storeRevisions
      : Array.isArray(offer?.revisions)
        ? offer.revisions
        : [];

  const revisionId = useMemo(
    () => resolveSubmittableRevisionId(offer, revisions),
    [offer, revisions]
  );

  const [reviewerId, setReviewerId] = useState(null);
  const [reviewerSelected, setReviewerSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setReviewerId(null);
    setReviewerSelected(null);
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!offer?.id || !revisionId) {
      toast.error(t("offers.submit_review.missing_revision"));
      return;
    }
    if (!revisionsEnabled) {
      toast.error(t("offers.approval.unavailable"));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {};
      if (reviewerId) payload.reviewer_id = reviewerId;

      const result = await dispatch(
        submitOfferRevisionForReviewAsync({
          offerId: offer.id,
          revisionId,
          ...payload,
        })
      );

      if (submitOfferRevisionForReviewAsync.rejected.match(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result.payload,
            result.payload?.missing
              ? "offers.approval.api_unavailable"
              : "offers.submit_review.failed"
          )
        );
        return;
      }

      toast.success(
        result.payload?.message || t("offers.submit_review.success")
      );
      await onRefresh?.(result.payload);
      setReviewerId(null);
      setReviewerSelected(null);
      onClose?.();
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.submit_review.failed")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={handleClose}
      title={t("offers.submit_review.title")}
      size="md"
      closeOnOutsideClick={!submitting}
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          {t("offers.submit_review.description")}
        </p>

        <EmployeeSelector
          key={open ? "submit-review-open" : "submit-review-closed"}
          name="reviewer_id"
          label={t("offers.submit_review.reviewer_label")}
          selected={reviewerSelected}
          setFieldValue={(_name, value) => setReviewerId(value || null)}
          onChange={(e) => {
            const id = e?.target?.value || null;
            setReviewerId(id);
            const obj = e?.target?.object;
            setReviewerSelected(
              id && obj
                ? { id: obj.id || id, displayTitle: obj.displayTitle || obj.name }
                : null
            );
          }}
        />
        <p className="text-xs text-slate-500">
          {t("offers.submit_review.reviewer_optional_hint")}
        </p>

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {t("Close")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !revisionId}
            className="inline-flex justify-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? t("Loading...") : t("offers.submit_review.confirm")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
